/**
 * TenantDebtChart
 *
 * Small line chart showing debt evolution for a single tenant (odooPartnerId).
 * Used inside the DossierDetailSheet to show how debt has built up over time.
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { ChartLineUp } from '@phosphor-icons/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

interface SnapshotDataPoint {
  period: string;
  periodLabel: string;
  totalDebt: number;
  activeCases: number;
  avgDaysOverdue: number;
}

interface SnapshotTimeSeries {
  granularity: string;
  data: SnapshotDataPoint[];
}

const formatEuro = (amount: number): string =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

interface TenantDebtChartProps {
  odooPartnerId: number;
  tenantName?: string;
}

export const TenantDebtChart = ({ odooPartnerId }: TenantDebtChartProps) => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const chartRef = useRef<any>(null);

  const [timeSeries, setTimeSeries] = useState<SnapshotTimeSeries | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTimeSeries = useCallback(async () => {
    if (!sessionRef.current || !odooPartnerId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        granularity: 'day',
        odooPartnerId: String(odooPartnerId),
      });
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/snapshots/time-series?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch tenant time series');
      const data: SnapshotTimeSeries = await response.json();
      setTimeSeries(data);
    } catch (error) {
      console.error('[TenantDebtChart] Error fetching time series:', error);
    } finally {
      setIsLoading(false);
    }
  }, [odooPartnerId]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  const chartData = useMemo(() => {
    if (!timeSeries?.data || timeSeries.data.length === 0) return null;

    return {
      labels: timeSeries.data.map((d) => d.periodLabel),
      datasets: [
        {
          label: 'Openstaand',
          data: timeSeries.data.map((d) => d.totalDebt),
          borderColor: '#EA8162',
          backgroundColor: 'rgba(234, 129, 98, 0.15)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 5,
          pointBackgroundColor: '#EA8162',
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          borderWidth: 2,
        },
      ],
    };
  }, [timeSeries]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#31403B',
          titleColor: '#F9F0EE',
          bodyColor: '#F9F0EE',
          borderColor: '#EA8162',
          borderWidth: 1,
          padding: 8,
          titleFont: { size: 11 },
          bodyFont: { size: 10 },
          callbacks: {
            label: (context: any) => {
              return `Schuld: ${formatEuro(context.parsed.y)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 9 },
            maxRotation: 0,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(49, 64, 59, 0.08)' },
          ticks: {
            font: { size: 9 },
            callback: (value: any) => formatEuro(value),
          },
        },
      },
    }),
    [],
  );

  // Compute min/max/delta for summary
  const firstPoint = timeSeries?.data?.[0];
  const latestPoint = timeSeries?.data?.[timeSeries.data.length - 1];
  const debtDelta = firstPoint && latestPoint
    ? latestPoint.totalDebt - firstPoint.totalDebt
    : null;
  const maxDebt = timeSeries?.data
    ? Math.max(...timeSeries.data.map((d) => d.totalDebt))
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 text-xs text-neutral-secondary-mid">
        Schuldhistoriek laden...
      </div>
    );
  }

  if (!timeSeries?.data || timeSeries.data.length < 2) {
    return (
      <div className="flex items-center justify-center py-4 text-xs text-neutral-secondary-mid italic">
        Nog onvoldoende snapshot data voor deze huurder. Na meerdere dagen verschijnt hier de schuldopbouw.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Summary line */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-neutral-secondary-mid">
          <ChartLineUp weight="duotone" size={14} />
          <span>Schuldopbouw</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-neutral-secondary-mid">
            Piek: <span className="font-semibold text-neutral-700">{formatEuro(maxDebt)}</span>
          </span>
          {debtDelta !== null && (
            <span
              className={`font-semibold ${
                debtDelta > 0
                  ? 'text-red-600'
                  : debtDelta < 0
                    ? 'text-green-600'
                    : 'text-neutral-500'
              }`}
            >
              {debtDelta > 0 ? '+' : ''}
              {formatEuro(debtDelta)} totaal
            </span>
          )}
        </div>
      </div>

      {/* Chart */}
      {chartData && (
        <div className="h-[120px] w-full">
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};
